/**
 * EHOVaultButton – The EHO "Panic" Button
 *
 * Persistent, high-visibility orange button that generates a
 * Secure Compliance Passport PDF on-demand.
 *
 * The PDF contains: HACCP pack, Thermal Logs (75°C proof),
 * 14-point Allergen Matrix, Training Heatmap, Pest Control,
 * and the last 7 days of Incident Logs.
 */
import React, { useState } from 'react';
import { ShieldCheck, Loader2, FileDown, AlertTriangle, ListChecks, Mail } from 'lucide-react';
import { ehoAPI, kioskAPI } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';

export default function EHOVaultButton({ venueId, venueName, compact = false, kioskUserId = null }) {
  const [state, setState] = useState('idle'); // idle | loading | error
  const [errMsg, setErrMsg] = useState('');
  const [panicModeOpen, setPanicModeOpen] = useState(false);
  const [panicModeLoading, setPanicModeLoading] = useState(false);
  const [panicModeData, setPanicModeData] = useState(null);
  const [emailSending, setEmailSending] = useState(false);

  const loadPanicMode = async () => {
    if (!venueId || panicModeLoading) return;
    setPanicModeLoading(true);
    setErrMsg('');
    try {
      const response = kioskUserId
        ? await kioskAPI.getPanicMode(venueId, kioskUserId)
        : await ehoAPI.getPanicMode(venueId);
      setPanicModeData(response?.data || null);
      setPanicModeOpen(true);
    } catch (error) {
      setErrMsg(error?.response?.data?.detail || 'Failed to load EHO mode data');
    } finally {
      setPanicModeLoading(false);
    }
  };

  const sendEmailFallback = async () => {
    if (!venueId || emailSending) return;
    const toEmail = window.prompt('Enter email address for EHO document delivery:');
    if (!toEmail) return;

    setEmailSending(true);
    try {
      if (kioskUserId) {
        await kioskAPI.emailPassport(venueId, kioskUserId, toEmail);
      } else {
        await ehoAPI.emailPassport(venueId, toEmail);
      }
      setErrMsg('Document emailed successfully.');
    } catch (error) {
      setErrMsg(error?.response?.data?.detail || 'Email delivery failed.');
    } finally {
      setEmailSending(false);
    }
  };

  const handleGenerate = async () => {
    if (state === 'loading') return;
    setState('loading');
    setErrMsg('');
    try {
      const res = kioskUserId
        ? await kioskAPI.generatePassport(venueId, kioskUserId)
        : await ehoAPI.generatePassport(venueId);
      const { pdf_base64, filename } = res.data;

      // Decode and trigger browser download
      const byteCharacters = atob(pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = filename || `RTB_Compliance_Passport_${venueId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setState('idle');
    } catch (err) {
      setErrMsg(err?.response?.data?.detail || 'Download failed. Use email fallback.');
      setState('error');
      setTimeout(() => setState('idle'), 5000);
    }
  };

  const renderPanicList = () => {
    const sections = [
      { key: 'food_safety_policies', label: 'Food Safety Policies' },
      { key: 'cleaning_logs', label: 'Cleaning Logs' },
      { key: 'temperature_logs', label: 'Temperature Logs' },
      { key: 'incident_logs', label: 'Incident Logs' },
      { key: 'compliance_certificates', label: 'Compliance Certificates' },
    ];

    return (
      <Dialog open={panicModeOpen} onOpenChange={setPanicModeOpen}>
        <DialogContent className="max-w-2xl bg-[#121212] border border-[#3F3F46] text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold uppercase tracking-tight text-white">
              {venueName ? `${venueName} EHO Mode` : 'EHO Mode'}
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Quick access documentation for Environmental Health Officer review.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.map((section) => {
              const count = panicModeData?.[section.key]?.length || 0;
              return (
                <div key={section.key} className="bg-[#1E1E1E] border border-[#3F3F46] rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-white text-sm font-semibold break-words">{section.label}</p>
                    <span className="text-[#FF6600] text-xs font-bold shrink-0">{count}</span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {count > 0 ? (
                      panicModeData?.[section.key]?.map((item, index) => (
                        <div key={`${section.key}-${item.id || index}`} className="rounded-sm border border-[#3F3F46] p-2 bg-[#111111]">
                          <p className="text-sm text-white break-words">
                            {item.name || item.template_name || item.certificate_name || item.location || item.incident_type || 'Document'}
                          </p>
                          <p className="text-[11px] text-[#A1A1AA] break-words mt-1">
                            {item.updated_at || item.created_at || item.occurred_at || item.expiry_date || 'No date available'}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-[#71717A]">No records available.</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={handleGenerate}
              disabled={state === 'loading'}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white border transition-all duration-200 ${state === 'loading' ? 'bg-[#3F3F46] border-[#52525B]' : 'bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46]'}`}
            >
              {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Generate PDF
            </button>
            <button
              onClick={sendEmailFallback}
              disabled={emailSending}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-[#FF6600]/40 bg-[#FF6600]/10 text-[#FFB380] hover:bg-[#FF6600]/20 disabled:opacity-60"
            >
              {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              Email Document
            </button>
          </div>

          {errMsg && (
            <p className={`text-xs text-center ${state === 'error' ? 'text-red-400' : 'text-[#A1A1AA]'}`}>
              {errMsg}
            </p>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={loadPanicMode}
            disabled={panicModeLoading}
            title="Start EHO mode"
            className="flex items-center gap-2 px-3 py-2 bg-[#FF6600] hover:bg-[#e55a00] disabled:bg-[#FF6600]/50 text-white font-bold rounded-lg text-sm transition-colors"
          >
            {panicModeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />}
            START EHO MODE
          </button>
          <button
            onClick={handleGenerate}
            disabled={state === 'loading'}
            title="Generate EHO document"
            className="flex items-center gap-2 px-3 py-2 bg-[#27272A] hover:bg-[#3F3F46] disabled:bg-[#27272A]/50 text-white font-bold rounded-lg text-sm transition-colors"
          >
            {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            Generate PDF
          </button>
        </div>
        {renderPanicList()}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={loadPanicMode}
        disabled={panicModeLoading}
        className="relative w-full flex items-center justify-center gap-3 py-5 rounded-2xl font-black text-xl text-white tracking-wide shadow-[0_0_40px_rgba(255,102,0,0.4)] border-2 transition-all duration-200 active:scale-95 bg-[#FF6600] border-[#FF6600] hover:bg-[#e55a00] hover:shadow-[0_0_60px_rgba(255,102,0,0.6)]"
      >
        {panicModeLoading ? <Loader2 className="w-7 h-7 animate-spin" /> : <ListChecks className="w-7 h-7" />}
        <span>START EHO MODE</span>
      </button>

      {renderPanicList()}

      <button
        onClick={handleGenerate}
        disabled={state === 'loading'}
        className={`relative w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold text-base text-white tracking-wide border transition-all duration-200 ${state === 'loading' ? 'bg-[#3F3F46] border-[#52525B]' : state === 'error' ? 'bg-red-700 border-red-600' : 'bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46]'}`}
      >
        {state === 'loading' ? <Loader2 className="w-5 h-5 animate-spin" /> : state === 'error' ? <AlertTriangle className="w-5 h-5" /> : <FileDown className="w-5 h-5" />}
        <span>{state === 'loading' ? 'Generating Document…' : 'Download EHO Document'}</span>
      </button>

      {(state === 'error' || errMsg?.toLowerCase().includes('email')) && (
        <button
          onClick={sendEmailFallback}
          disabled={emailSending}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-[#FF6600]/40 bg-[#FF6600]/10 text-[#FFB380] hover:bg-[#FF6600]/20 disabled:opacity-60"
        >
          {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          Email Document Instead
        </button>
      )}

      {state === 'error' && errMsg && (
        <p className="text-red-400 text-xs text-center">{errMsg}</p>
      )}
      {state !== 'error' && errMsg && (
        <p className="text-[#A1A1AA] text-xs text-center">{errMsg}</p>
      )}
      <p className="text-[#52525B] text-xs text-center">
        {venueName ? `${venueName} · ` : ''}Compliance Passport · HACCP · Allergens · Training · Last 7 Days
      </p>
    </div>
  );
}
